/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Popover } from 'foundations-components/transitional/components/Popover';
import PropTypes from 'prop-types';
import get from 'transmute/get';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import ImmutablePropTypes from 'react-immutable-proptypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import { getCallProvidersFromOmnibusResult } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import PhoneFromNumberOptions from './PhoneFromNumberOptions';
import CallDeviceOptions from './CallDeviceOptions';
import * as CallStatus from 'calling-internal-common/widget-status/constants/CallWidgetStates';
import getCallingCapabilities from 'calling-global-api/methods/getCallingCapabilities';
import setCallingMethod from 'calling-global-api/methods/setCallingMethod';
import setCallingProviderSettings from 'calling-global-api/methods/setCallingProviderSettings';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { CALL_FROM_PHONE } from 'calling-internal-common/constants/CallMethods';
import { getIsHubSpotProvidedNumber } from 'calling-lifecycle-internal/operators/from-number/getIsHubSpotProvidedNumber';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CallSettingWrapper = styled.div.withConfig({
  displayName: "CurrentSettingsInfo__CallSettingWrapper",
  componentId: "sc-1x6t9pm-0"
})(({
  theme
}) => `
  cursor: pointer;
  background-color: transparent;
  &.is--disabled {
    opacity: 0.5;
  }
  &:hover {
    background-color: ${theme.color['fill-surface-default-hover']};
  }
  `);
function CurrentSettingsInfo({
  appIdentifier,
  asyncSettingsOmnibus,
  selectedCallProvider,
  selectedFromNumber,
  selectedCallMethod,
  widgetState,
  onSetCallStatus,
  onSetThirdPartyActive,
  disabled,
  showFromNumberDropdown
}) {
  const providerName = useMemo(() => get('name', selectedCallProvider), [selectedCallProvider]);
  const [showPhoneFromNumberOptions, setShowPhoneFromNumberOptions] = useState(false);
  const [showDeviceOptions, setShowDeviceOptions] = useState(false);
  const [hasCallingProvidersCapability, setHasCallingProvidersCapability] = useState(true);
  useEffect(() => {
    getCallingCapabilities().then(({
      callingProviders
    }) => {
      setHasCallingProvidersCapability(callingProviders);
    });
  }, []);
  const handleShowPhoneFromNumberOptions = useCallback(() => {
    setShowPhoneFromNumberOptions(true);
    setShowDeviceOptions(false);
  }, []);
  const handleShowDeviceOptionsOptions = useCallback(() => {
    setShowDeviceOptions(true);
    setShowPhoneFromNumberOptions(false);
  }, []);
  const resetWidget = useCallback(() => {
    const callStatus = widgetState.get('callStatus');
    const isThirdPartyActive = widgetState.get('isThirdPartyActive');
    if (callStatus === CallStatus.ENDED) {
      onSetCallStatus(CallStatus.READY);
    }
    if (isThirdPartyActive) {
      onSetThirdPartyActive(false);
    }
  }, [widgetState, onSetCallStatus, onSetThirdPartyActive]);
  const handleSelectFromNumber = useCallback(key => {
    resetWidget();
    const selectedProviderName = selectedCallProvider.get('name');
    const nextProviderName = key.providerName;
    const nextFromNumber = key.fromNumber;
    const omnibusData = getData(asyncSettingsOmnibus);
    const callingProviders = getCallProvidersFromOmnibusResult(omnibusData);
    const callingProvider = callingProviders.find(provider => get('name', provider) === nextProviderName);
    if (selectedProviderName !== nextProviderName) {
      CommunicatorLogger.log('calling_ProviderSwitch', {
        action: 'provider switch',
        activity: 'call',
        channel: 'outbound call',
        switchFrom: selectedProviderName,
        switchTo: nextProviderName,
        switchSource: 'numbers popover'
      });
    }
    if (selectedFromNumber !== nextFromNumber) {
      CommunicatorLogger.log('Calling_DropDownInteractions', {
        action: 'change "from" number',
        source: appIdentifier,
        selectFromNumberType: nextFromNumber && nextFromNumber.get('source'),
        previousFromNumberType: selectedFromNumber && selectedFromNumber.get('source')
      });
    }
    setCallingProviderSettings({
      callingProvider,
      fromNumber: nextFromNumber
    });
  }, [appIdentifier, asyncSettingsOmnibus, resetWidget, selectedCallProvider, selectedFromNumber]);
  const handleSelectDevice = useCallback(method => {
    resetWidget();
    setCallingMethod(method);
    if (selectedCallMethod !== method) {
      CommunicatorLogger.log('Calling_DropDownInteractions', {
        action: 'change calling device',
        source: appIdentifier,
        previousDeviceType: selectedCallMethod,
        selectDeviceType: method
      });
    }
  }, [appIdentifier, resetWidget, selectedCallMethod]);
  const isHubSpotProvidedNumber = useMemo(() => getIsHubSpotProvidedNumber(selectedFromNumber), [selectedFromNumber]);
  const isCallingFromPhoneOnHubspotProvidedNumber = useMemo(() => isHubSpotProvidedNumber && selectedCallMethod === CALL_FROM_PHONE, [isHubSpotProvidedNumber, selectedCallMethod]);
  let numberOrProvider = providerName;
  if (selectedFromNumber) {
    if (selectedFromNumber.get('nickname') && selectedFromNumber.get('nickname').length) {
      numberOrProvider = `${selectedFromNumber.get('nickname')} ${selectedFromNumber.get('friendlyName')}`;
    } else if (selectedFromNumber.get('friendlyName')) {
      numberOrProvider = selectedFromNumber.get('friendlyName');
    }
  }
  return numberOrProvider ? /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    borderBlockEndWidth: "200",
    borderBlockEndStyle: "solid",
    borderBlockEndColor: "border-core-disabled",
    as: "div",
    children: [/*#__PURE__*/_jsx(Popover, {
      open: showPhoneFromNumberOptions,
      onOpenChange: () => setShowPhoneFromNumberOptions(false),
      animateOnToggle: true,
      content: {
        body: /*#__PURE__*/_jsx(Tooltip, {
          disabled: !disabled,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `callSettingsWidget.disableTooltip`
          }),
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            as: "div",
            marginBlockStart: "-500",
            marginInline: "-600",
            children: /*#__PURE__*/_jsx(PhoneFromNumberOptions, {
              asyncSettingsOmnibus: asyncSettingsOmnibus,
              selectedProviderName: providerName,
              selectedFromNumber: selectedFromNumber,
              hasCallingProvidersCapability: hasCallingProvidersCapability,
              onSelect: handleSelectFromNumber,
              disabled: disabled
            })
          })
        })
      },
      inlineSize: "300px",
      placement: "right bottom",
      arrowSize: "none",
      children: showFromNumberDropdown && /*#__PURE__*/_jsx(CallSettingWrapper, {
        onClick: handleShowPhoneFromNumberOptions,
        "data-selenium-test": "open-pre-call-popup-from-numbers",
        children: /*#__PURE__*/_jsxs(Flex, {
          paddingInline: "400",
          paddingBlock: "300",
          justifyContent: "space-between",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "callSettingsWidget.callFrom",
              options: {
                numberOrProvider
              }
            })
          }), /*#__PURE__*/_jsx(RightIcon, {
            size: "300"
          })]
        })
      })
    }), selectedCallMethod && /*#__PURE__*/_jsx(Popover, {
      open: showDeviceOptions,
      onOpenChange: () => setShowDeviceOptions(false),
      animateOnToggle: true,
      content: {
        body: /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          as: "div",
          marginBlockStart: "-500",
          marginInline: "-600",
          children: /*#__PURE__*/_jsx(CallDeviceOptions, {
            device: selectedCallMethod,
            onSelect: handleSelectDevice,
            disabled: disabled,
            isHubSpotProvidedNumber: isHubSpotProvidedNumber
          })
        })
      },
      placement: "right bottom",
      arrowSize: "none",
      children: /*#__PURE__*/_jsx(CallSettingWrapper, {
        selected: selectedCallMethod,
        onClick: handleShowDeviceOptionsOptions,
        "data-selenium-test": "open-pre-call-popup-devices",
        children: /*#__PURE__*/_jsxs(Flex, {
          paddingInline: "400",
          paddingBlock: "300",
          justifyContent: "start",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `callSettingsWidget.device.${selectedCallMethod}`
            })
          }), isCallingFromPhoneOnHubspotProvidedNumber && /*#__PURE__*/_jsx(Tooltip, {
            placement: "bottom",
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "callee-phone-numbers.switchToCallFromBrowser"
            }),
            children: /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              marginInlineStart: "200",
              children: /*#__PURE__*/_jsx(WarningIcon, {
                "data-selenium-test": "call-from-phone-with-hubspot-number-warning",
                color: "do-not-use-candy-apple",
                size: "300"
              })
            })
          }), /*#__PURE__*/_jsx(View, {
            marginInlineStart: "auto",
            children: /*#__PURE__*/_jsx(RightIcon, {
              size: "300"
            })
          })]
        })
      })
    })]
  }) : null;
}
CurrentSettingsInfo.propTypes = {
  appIdentifier: PropTypes.string,
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  selectedCallProvider: RecordPropType('CallingProvider'),
  selectedFromNumber: RecordPropType('FromNumber'),
  selectedCallMethod: PropTypes.string,
  widgetState: ImmutablePropTypes.map.isRequired,
  onSetCallStatus: PropTypes.func.isRequired,
  onSetThirdPartyActive: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showFromNumberDropdown: PropTypes.bool
};
export default /*#__PURE__*/memo(CurrentSettingsInfo);